import { NEW_STORY_ID } from '@/constants/DEFAULT_VALUES.ts'
import { STORY_TYPES } from '@/constants/STORY_TYPES.ts'
import { errorLink, mainSplitLink, retryLink } from '@/requests/apollo/link.ts'
import { getNow } from '@/utils/date.ts'
import { isTemporaryId } from '@/utils/key.ts'
import { isAccepted, isInProgress, isUnscheduled, isUnstarted } from '@/utils/status.ts'
import { ApolloClient, InMemoryCache, type Reference, from } from '@apollo/client'
import type { ReadFieldFunction } from '@apollo/client/cache/core/types/common'

export const apolloInMemoryCache = new InMemoryCache({
  typePolicies: {
    Story: {
      fields: {
        owners: {
          merge: useIncoming,
        },
        requestedBy: {
          merge: useIncoming,
        },
        tasks: {
          merge: useIncoming,
        },
        labels: {
          merge: useIncoming,
          read(existing, { readField }) {
            return sortBy(existing ?? [], 'name', readField)
          },
        },
        comments: {
          merge: useIncoming,
        },
      },
    },
    Comment: {
      fields: {
        attachments: {
          merge: useIncoming,
        },
      },
    },
    Iteration: {
      keyFields: ['number', 'projectId'],
    },
    Project: {
      fields: {
        stories: {
          // biome-ignore lint/style/useDefaultParameterLast: This is defined by Apollo
          merge(existing: Reference[] = [], incoming: Reference[] = [], { readField }) {
            const newSet = mergeUniqueByRef(
              existing.filter((story) => {
                const storyId = readField('id', story) as string
                if (!storyId) return false
                const acceptedAt = readField('acceptedAt', story) as number
                return isTemporaryId(storyId) || acceptedAt
              }),
              incoming,
            )

            return Array.from(newSet.values())
          },
        },
        iterations: {
          merge(existing, incoming, { readField }) {
            const newSet = mergeUniqueByRef(existing, incoming)
            return sortBy(newSet, 'number', readField)
          },
        },
        labels: {
          merge: useIncoming,
          read(existing, { readField }) {
            return sortBy(existing ?? [], 'name', readField)
          },
        },
        relevant: {
          read(_, { readField }) {
            const iteration = getCurrentIteration(readField)
            return filterByStatus([isAccepted, isInProgress, isUnstarted], readField).filter((story) => {
              const storyStatus = readField<StoryStatus>('status', story)
              if (!storyStatus) return false
              if (iteration && isAccepted(storyStatus)) {
                const acceptedAt = readField<number | null>('acceptedAt', story)
                const iterationStart = readField<number>('start', iteration)
                if (acceptedAt && iterationStart && acceptedAt < iterationStart) return false
              }
              return readField('id', story) !== NEW_STORY_ID
            })
          },
        },
        unscheduled: {
          read(_, { readField }) {
            const newStory = (readField<Reference[]>('stories') || []).find(
              (story) =>
                readField('id', story) === NEW_STORY_ID &&
                [STORY_TYPES.RELEASE, STORY_TYPES.BUG, STORY_TYPES.CHORE].some(
                  (storyType) => readField('storyType', story) === storyType,
                ),
            )
            return newStory
              ? [newStory, ...filterByStatus([isUnscheduled], readField)]
              : filterByStatus([isUnscheduled], readField)
          },
        },
        done: {
          read(_, { readField }) {
            const iteration = getCurrentIteration(readField)
            return filterByStatus([isAccepted], readField).filter((story) => {
              const storyStatus = readField<StoryStatus>('status', story)
              if (!storyStatus) return false
              if (iteration && isAccepted(storyStatus)) {
                const acceptedAt = readField<number | null>('acceptedAt', story)
                const iterationStart = readField<number>('start', iteration)
                if (acceptedAt && iterationStart && acceptedAt >= iterationStart) return false
              }
              return true
            })
          },
        },
        currentIteration: {
          read(_, { readField }) {
            return getCurrentIteration(readField)
          },
        },
        iterationDeadlines: {
          read(_, { readField }) {
            const releaseStories = (readField<Reference[]>('stories') || [])
              .filter((story) => {
                const storyType = readField<StoryType>('storyType', story)
                const deadline = readField<number>('deadline', story)
                const status = readField<StoryStatus>('status', story)
                return storyType === STORY_TYPES.RELEASE && deadline && status && !isUnscheduled(status)
              })
              .toSorted((a, b) => (readField<number>('deadline', a) ?? 0) - (readField<number>('deadline', b) ?? 0))

            const map = {} as Record<number, Deadline[]>
            const iterations = readField<Iteration[]>('iterations') || []

            for (let iterationIndex = 0; iterationIndex < iterations.length; iterationIndex++) {
              const iteration = iterations[iterationIndex]
              const iterationNumber = readField<number>('number', iteration) ?? 0
              const start = readField<number>('start', iteration) ?? 0
              const finish = readField<number>('finish', iteration) ?? 0

              for (let storyIndex = 0; storyIndex < releaseStories.length; storyIndex++) {
                const story = releaseStories[storyIndex]
                const deadline = readField<number>('deadline', story) ?? 0
                const title = readField<string>('title', story) ?? ''

                if (deadline >= start && deadline < finish) {
                  if (!map[iterationNumber]) map[iterationNumber] = []
                  map[iterationNumber].push({ title, deadline })
                } else if (deadline >= finish) {
                  break
                }
              }
            }

            const result: IterationDeadlines[] = []
            for (const [key, deadlines] of Object.entries(map)) {
              result.push({
                iterationNumber: Number(key),
                deadlines,
              })
            }

            return result
          },
        },
      },
    },
  },
})

function mergeUniqueByRef<T extends { __ref: string }>(existing: T[] = [], incoming: T[] = []): T[] {
  const map = new Map<string, T>()

  if (!incoming) return Array.from(map.values())

  for (const item of incoming) {
    map.set(item.__ref, item)
  }

  for (const item of existing) {
    map.set(item.__ref, item)
  }

  return Array.from(map.values())
}

function useIncoming<T>(_: T, incoming: T): T {
  return incoming
}

function sortBy<T extends Reference>(array: T[], key: string, readField: ReadFieldFunction): T[] {
  return (array as T[]).toSorted((a, b) => {
    const elA = readField<string | null>(key, a) || ''
    const elB = readField<string | null>(key, b) || ''
    return elA > elB ? 1 : -1
  })
}

function getCurrentIteration(readField: ReadFieldFunction): Iteration | undefined {
  return (readField<Iteration[]>('iterations') ?? []).find((iteration) => {
    const start = readField<number>('start', iteration) ?? 0
    const finish = readField<number>('finish', iteration) ?? 0
    return start <= getNow() && finish > getNow()
  })
}

function filterByStatus(conditions: ((status: StoryStatus) => boolean)[], readField: ReadFieldFunction): Reference[] {
  return (readField<Reference[]>('stories') || []).filter((story) => {
    const storyStatus = readField<StoryStatus>('status', story)
    if (!storyStatus) return false
    return conditions.some((condition: (status: StoryStatus) => boolean) => condition(storyStatus))
  })
}

export const apolloClient = new ApolloClient({
  cache: apolloInMemoryCache,
  link: from([retryLink, errorLink, mainSplitLink]),
})
